import { useEffect, useState, useMemo, useCallback } from "react";
import { PlusCircleOutlined, MoreOutlined } from "@ant-design/icons";
import { Spin, Modal, Button, Dropdown } from "antd";
import TableComponent from "../../components/TableComponent";
import { createColumn } from "../../utils/tableUtils";
import { useNavigate } from "react-router-dom";
import MainButtonComponent from "../../components/MainButtonComponent";
import SelectComponent from "../../components/SelectComponent";
import {
	HeaderComponent,
	Container,
	ContainerHeader,
	ActionButtonOverlay,
	FiltersContainer,
} from "../../components/styled/common";
import { useMobileAppVersionContext } from "../../context/MobileAppVersionContext/MobileAppVersionContext";
import { APP_OPTIONS, PLATFORM_OPTIONS, FORCE_UPDATE_OPTIONS } from "./constants";

const MobileAppVersionList = () => {
	const [tableData, setTableData] = useState<any[]>([]);
	const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null);
	const [selectedAppId, setSelectedAppId] = useState<string | null>(null);
	const [selectedForceUpdate, setSelectedForceUpdate] = useState<string | null>(null);
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [currentItem, setCurrentItem] = useState<any>(null);
	const { clearMobileAppVersionList, operationStatus, removeMobileAppVersion, versionList, getMobileAppVersionList } = useMobileAppVersionContext();

	const navigate = useNavigate();

	useEffect(() => {
		clearMobileAppVersionList();
		getMobileAppVersionList();

		return () => {
			clearMobileAppVersionList();
		};
	}, []);

	useEffect(() => {
		const filteredData = versionList.filter((item) => {
			const platformMatch = !selectedPlatform || item.platform === selectedPlatform;
			const appIdMatch = !selectedAppId || Number(item.appId) === Number(selectedAppId);
			const forceUpdateMatch = selectedForceUpdate === null || item.forceUpdate === (selectedForceUpdate === "true");

			return platformMatch && appIdMatch && forceUpdateMatch;
		});

		setTableData(filteredData);
	}, [versionList, selectedPlatform, selectedAppId, selectedForceUpdate]);

	const getAppLabel = useCallback((appValue: string | undefined) => {
		const app = APP_OPTIONS.find(a => Number(a.value) === Number(appValue));
		return app?.label ?? "";
	}, []);

	const columns = useMemo(() => [
		createColumn({
			title: "Platform",
			dataIndex: "platform",
			width: 30,
			sorterType: "string",
		}),
		createColumn({
			title: "Sürüm",
			dataIndex: "version",
			width: 50,
			sorterType: "version",
		}),
		createColumn({
			title: "Force Update",
			dataIndex: "forceUpdate",
			width: 50,
			sorterType: "boolean",
			render: (text, record) => (
				<span>{record.forceUpdate ? "Açık" : "Kapalı"}</span>
			),
		}),
		createColumn({
			title: "Uygulama Adı",
			dataIndex: "appId",
			width: 50,
			sorterType: "string",
			render: (text) => getAppLabel(text),
		}),
		createColumn({
			title: "",
			dataIndex: "action",
			width: 20,
			render: (text, record) => {
				const menuItems = [
					{
						key: "edit",
						label: "Güncelle",
						onClick: () => navigate(`/edit-mobile-app-version/${record.versionId}`),
					},
					{
						key: "delete",
						label: "Sil",
						onClick: () => {
							setCurrentItem(record);
							setIsModalVisible(true);
						},
					},
				];

				return (
					<Dropdown menu={{ items: menuItems }} trigger={["click"]}>
						<MoreOutlined />
					</Dropdown>
				);
			},
		}),
	], [navigate, getAppLabel]);


	return (
		<div>
			<HeaderComponent>Mobil Uygulama Sürümleri</HeaderComponent>
			<Container>
				<Spin spinning={operationStatus.type === "loading" && operationStatus.operation === "fetch"}>
					<ContainerHeader>
						<FiltersContainer>
							<SelectComponent
								allowClear
								placeholder="Platform Seç"
								style={{ width: "9.375vw" }}
								options={PLATFORM_OPTIONS}
								value={selectedPlatform}
								onChange={(value) => setSelectedPlatform(value != null ? String(value) : null)}
							/>
							<SelectComponent
								allowClear
								placeholder="Uygulama Seç"
								style={{ width: "9.375vw" }}
								options={APP_OPTIONS}
								value={selectedAppId}
								onChange={(value) => setSelectedAppId(value != null ? String(value) : null)}
							/>
							<SelectComponent
								allowClear
								placeholder="Force Update"
								style={{ width: "9.375vw" }}
								options={FORCE_UPDATE_OPTIONS}
								value={selectedForceUpdate}
								onChange={(value) => setSelectedForceUpdate(value != null ? String(value) : null)}
							/>
						</FiltersContainer>
						<ActionButtonOverlay>
							<MainButtonComponent
								style={{ marginRight: "15px" }}
								icon={<PlusCircleOutlined />}
								onClick={() => navigate("/add-mobile-app-version")}
							>
								Yeni Ekle
							</MainButtonComponent>
						</ActionButtonOverlay>
					</ContainerHeader>
				</Spin>
				{!(operationStatus.type === "loading" && operationStatus.operation === "fetch") && (
					<TableComponent
						columns={columns}
						dataSource={tableData}
					/>
				)}
			</Container>
			<Modal
				title="Mobil Uygulama Sürümünü Sil"
				open={isModalVisible}
				onCancel={() => setIsModalVisible(false)}
				centered
				footer={[
					<Button key="back" type="text" onClick={() => setIsModalVisible(false)}>
						İptal
					</Button>,
					<Button
						loading={operationStatus.type === "loading" && operationStatus.operation === "delete"}
						key="submit"
						type="primary"
						danger
						onClick={async () => {
							await removeMobileAppVersion(currentItem?.versionId);
							setIsModalVisible(false);
						}}
					>
						Evet
					</Button>,
				]}
			>
				<p><strong>{`"${currentItem?.platform} v${currentItem?.version}"`}</strong> sürümünü silmek istediğinizden emin misiniz?</p>
			</Modal>
		</div>
	);
};

export default MobileAppVersionList;
